import { forwardRef, useId, useState, type CSSProperties } from 'react';
import { VCL_CONTROLS } from './controls.js';
import { useVcl } from './VclContext.js';
import type { VclControlId } from './types.js';

export interface VclDevPanelProps {
  readonly isBaseline: boolean;
}

const panelStyle: CSSProperties = {
  position: 'fixed',
  right: '0.75rem',
  bottom: '0.75rem',
  zIndex: 9999,
  width: 'min(22rem, calc(100vw - 1.5rem))',
  maxHeight: 'min(70vh, 32rem)',
  overflow: 'auto',
  padding: '0.75rem',
  borderRadius: '0.5rem',
  border: '1px solid var(--color-border, #e5e5e5)',
  background: 'var(--color-surface-elevated, #fff)',
  color: 'var(--color-on-surface, #000)',
  boxShadow: 'var(--shadow-popover, 0 6px 24px rgba(0,0,0,0.12))',
  fontFamily: 'ui-sans-serif, system-ui, sans-serif',
  fontSize: '0.8125rem',
  lineHeight: 1.4,
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.5rem',
  marginBottom: '0.5rem',
};

const rowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '2.5rem 1fr',
  gap: '0.35rem 0.5rem',
  alignItems: 'center',
  marginBottom: '0.55rem',
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.5rem',
  marginTop: '0.5rem',
};

const buttonStyle: CSSProperties = {
  minHeight: '2.75rem',
  minWidth: '2.75rem',
  padding: '0.35rem 0.75rem',
  borderRadius: '0.375rem',
  border: '1px solid var(--color-border, #d4d4d4)',
  background: 'var(--color-surface, #fff)',
  color: 'inherit',
  cursor: 'pointer',
  font: 'inherit',
};

/**
 * DEV-only VCL panel — 10 controls, Reset, Export.
 * Mounted only from `VclProvider` when opt-in gate passes.
 */
export const VclDevPanel = forwardRef<HTMLElement, VclDevPanelProps>(
  function VclDevPanel({ isBaseline }, ref) {
    const {
      state,
      effectiveState,
      clamps,
      setControl,
      resetToBaseline,
      exportPromotionJson,
    } = useVcl();
    const titleId = useId();
    const [collapsed, setCollapsed] = useState(false);
    const [copyStatus, setCopyStatus] = useState<string | null>(null);

    const handleExport = async (): Promise<void> => {
      const json = exportPromotionJson();
      try {
        await navigator.clipboard.writeText(json);
        setCopyStatus('Copied promotion JSON (effective post-clamp deltas).');
      } catch {
        // Fallback: downloadable blob when clipboard is blocked.
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'vcl-promotion-deltas.json';
        anchor.click();
        URL.revokeObjectURL(url);
        setCopyStatus('Downloaded vcl-promotion-deltas.json.');
      }
    };

    return (
      <aside
        ref={ref}
        style={panelStyle}
        role="region"
        aria-labelledby={titleId}
        data-testid="vcl-dev-panel"
      >
        <div style={headerStyle}>
          <h2 id={titleId} style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600 }}>
            Customer VCL (DEV)
          </h2>
          <button
            type="button"
            style={buttonStyle}
            aria-expanded={!collapsed}
            aria-controls="vcl-dev-panel-body"
            onClick={() => setCollapsed((v) => !v)}
          >
            {collapsed ? 'Expand' : 'Collapse'}
          </button>
        </div>

        {!collapsed ? (
          <div id="vcl-dev-panel-body">
            <p style={{ margin: '0 0 0.5rem', opacity: 0.8 }}>
              {isBaseline
                ? 'At frozen live baseline (zero-delta).'
                : 'Tuned — DEV deltas ≠ approved tokens until DG promotion.'}
            </p>

            {VCL_CONTROLS.map((control) => {
              const labelId = `vcl-label-${control.id}`;
              const helpId = `vcl-help-${control.id}`;
              const value = state[control.id as VclControlId];
              const effective = effectiveState[control.id as VclControlId];
              return (
                <div key={control.id} style={rowStyle}>
                  <span id={labelId} style={{ fontWeight: 600 }}>
                    {control.id}
                  </span>
                  <div>
                    <label htmlFor={`vcl-range-${control.id}`} style={{ display: 'block' }}>
                      {control.label}
                    </label>
                    <input
                      id={`vcl-range-${control.id}`}
                      type="range"
                      min={control.min}
                      max={control.max}
                      step={1}
                      value={value}
                      aria-labelledby={labelId}
                      aria-describedby={helpId}
                      onChange={(event) => {
                        setControl(control.id, Number(event.target.value));
                      }}
                      style={{ width: '100%' }}
                    />
                    <div id={helpId} style={{ opacity: 0.75 }}>
                      step {value}
                      {effective !== value ? ` → effective ${effective}` : ''} · {control.stepUnit}
                    </div>
                  </div>
                </div>
              );
            })}

            {clamps.length > 0 ? (
              <ul
                aria-label="Invariant clamps applied"
                style={{ margin: '0.25rem 0 0.5rem', paddingLeft: '1.1rem' }}
              >
                {clamps.map((note) => (
                  <li key={`${note.controlId}-${note.reason}`}>
                    {note.controlId}: {note.reason}
                  </li>
                ))}
              </ul>
            ) : null}

            <div style={actionsStyle}>
              <button
                type="button"
                style={buttonStyle}
                aria-label="Reset all VCL controls to live baseline"
                onClick={() => {
                  resetToBaseline();
                  setCopyStatus(null);
                }}
              >
                Reset baseline
              </button>
              <button
                type="button"
                style={buttonStyle}
                aria-label="Export VCL JSON deltas mapped to promotion targets"
                onClick={() => {
                  void handleExport();
                }}
              >
                Export JSON
              </button>
            </div>
            {copyStatus !== null ? (
              <p role="status" style={{ margin: '0.5rem 0 0' }}>
                {copyStatus}
              </p>
            ) : null}
          </div>
        ) : null}
      </aside>
    );
  },
);

VclDevPanel.displayName = 'VclDevPanel';
